import React, { useState, useEffect, useRef, useCallback } from 'react';
import { pdfjs } from 'react-pdf';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useToast } from '../contexts/ToastContext';
import { usePDFViewer } from '../hooks/document/usePDFViewer';
import { useSignaturePlacement } from '../hooks/document/useSignaturePlacement';
import { useTextBlockPlacement } from '../hooks/document/useTextBlockPlacement';
import { useDocumentSigning } from '../hooks/document/useDocumentSigning';
import { usePlacementHandler } from '../hooks/document/usePlacementHandler';
import PDFViewer from './documentSigning/PDFViewer';
import SigningSidebar from './documentSigning/SigningSidebar';
import ConfirmationModal from './common/ConfirmationModal';
import BottomDrawer from './documentSigning/BottomDrawer';

if (typeof window !== 'undefined' && !pdfjs.GlobalWorkerOptions.workerSrc) {
  pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
}

function DocumentSigningModal({ isOpen, onClose, document }) {
  const { showToast } = useToast();
  const isPreviewMode = document?.status === 'Completed' || Boolean(document?.signedBy) || document?.meta?.is_signable === false;
  const useSignedPdf = document?.status === 'Completed' || Boolean(document?.signedBy);

  const [instructionsExpanded, setInstructionsExpanded] = useState(false);
  const [shouldShowTour, setShouldShowTour] = useState(false);
  const [showDiscardConfirmation, setShowDiscardConfirmation] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [hasOpenedDrawer, setHasOpenedDrawer] = useState(false);
  const [pdfContainerWidth, setPdfContainerWidth] = useState(() => {
    if (typeof window !== 'undefined') {
      const viewportWidth = window.innerWidth;
      if (viewportWidth < 768) {
        return Math.min(viewportWidth - 40, 600);
      } else if (viewportWidth < 1024) {
        return Math.min(viewportWidth - 60, 700);
      } else if (viewportWidth < 1285) {
        return Math.min(viewportWidth * 0.55, 750);
      } else {
        return Math.min(viewportWidth * 0.5, 800);
      }
    }
    return 800;
  });
  const pdfContainerRef = useRef(null);
  const driverObjRef = useRef(null);
  const hasScrolledForPlacement = useRef(false);
  const [placementTarget, setPlacementTarget] = useState(null);

  const {
    numPages,
    error,
    pdfUrl,
    isLoadingPdf,
    pdfPages,
    pdfPageRefs,
    docApiResponse,
    onDocumentLoadSuccess,
    onDocumentLoadError,
    handleRetry,
    registerPageRef,
  } = usePDFViewer(document, isOpen, useSignedPdf);

  const {
    textBlocks,
    isLoadingFields,
    defaultBlockPlacements,
    signatureDefaultPlacement,
    isSignatureRequired,
    handleTextBlockValueChange,
    handleResetTextPlacement,
    queuePlacementForBlock: queuePlacementForBlockBase,
    setTextBlocks,
  } = useTextBlockPlacement(pdfPages, isPreviewMode, docApiResponse);

  const signaturePlacement = useSignaturePlacement(signatureDefaultPlacement);
  const {
    signatureDraft,
    signaturePosition,
    signatureWidthPct,
    typedName,
    setTypedName,
    typedTemplates,
    typedSelectedTemplate,
    setTypedSelectedTemplate,
    buildSignatureImage,
    hasSignatureImage,
    resetSignaturePlacement,
    resetAll: resetSignatureState,
    setSignaturePosition,
  } = signaturePlacement;

  const {
    submitting,
    canSubmitDocument,
    handleSubmitDocument: handleSubmitDocumentBase,
  } = useDocumentSigning(document, pdfUrl, signatureDraft, signaturePosition, textBlocks, isSignatureRequired);

  const { handlePlacementClick } = usePlacementHandler(
    pdfPages,
    signatureWidthPct,
    placementTarget,
    setPlacementTarget,
    textBlocks,
    setTextBlocks,
    setSignaturePosition,
    showToast
  );

  const scrollToBottom = useCallback(() => {
    if (numPages === 0) return;
    const lastPageNumber = numPages;
    const element = pdfPageRefs.current[lastPageNumber];
    if (element) {
      setTimeout(() => {
        element.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }, 100);
    }
  }, [numPages, pdfPageRefs]);

  const isPageVisible = useCallback((pageNumber) => {
    const element = pdfPageRefs.current[pageNumber];
    const container = pdfContainerRef.current;
    
    if (!element || !container) return false;
    
    const pageRect = element.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    
    // Check if the page is visible in the viewport
    // Page is considered visible if any part of it is within the container viewport
    const isVisible = (
      pageRect.top < containerRect.bottom &&
      pageRect.bottom > containerRect.top
    );
    
    return isVisible;
  }, [pdfPageRefs, pdfContainerRef]);

  const scrollToPlacement = useCallback((placement, forceScroll = false) => {
    if (!placement || !placement.pageNumber || numPages === 0) return;

    const pageNumber = placement.pageNumber;
    const element = pdfPageRefs.current[pageNumber];
    const container = pdfContainerRef.current;

    if (!element || !container) return;

    if (!forceScroll && isPageVisible(pageNumber)) {
      return;
    }

    requestAnimationFrame(() => {
      const pageRect = element.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const relativeTop = pageRect.top - containerRect.top + container.scrollTop;
      const pageHeight = pageRect.height;

      let targetY = relativeTop;
      if (placement.yPct !== undefined) {
        targetY = relativeTop + (placement.yPct * pageHeight);
      }

      const viewportHeight = containerRect.height;
      const scrollOffset = targetY - (viewportHeight * 0.3);

      container.scrollTo({
        top: Math.max(0, scrollOffset),
        behavior: 'smooth'
      });
    });
  }, [numPages, pdfPageRefs, pdfContainerRef, isPageVisible]);

  const queuePlacementForBlock = useCallback((block, valueOverride) => {
    return queuePlacementForBlockBase(block, valueOverride, showToast, scrollToPlacement, scrollToBottom);
  }, [queuePlacementForBlockBase, showToast, scrollToPlacement, scrollToBottom]);

  const handleSubmitDocument = useCallback(() => {
    handleSubmitDocumentBase(showToast, onClose);
  }, [handleSubmitDocumentBase, showToast, onClose]);

  const handleSignNowClick = useCallback(() => {
    setIsDrawerOpen(true);
    setHasOpenedDrawer(true);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);

  const handleFindingPlacement = useCallback((target, blockId) => {
    if (target === 'signature') {
      const generatedImage = buildSignatureImage(false, showToast);
      const effectiveImage = generatedImage || signatureDraft;
      if (!effectiveImage) {
        return;
      }

      if (defaultBlockPlacements.signature) {
        scrollToPlacement(defaultBlockPlacements.signature, false);
        setSignaturePosition(defaultBlockPlacements.signature);
        showToast('Signature placement saved.', 'success');
        return;
      }

      setSignaturePosition(null);
      setPlacementTarget({ type: 'signature' });
      showToast('Click on the document to place your signature.', 'info');
      scrollToBottom();
      return;
    }
    if (target === 'text' && blockId) {
      const block = textBlocks.find((entry) => entry.id === blockId);
      if (!block) return;
      queuePlacementForBlock(block);
    }
  }, [buildSignatureImage, showToast, signatureDraft, defaultBlockPlacements, setSignaturePosition, scrollToPlacement, scrollToBottom, textBlocks, queuePlacementForBlock]);

  useEffect(() => {
    if (!isOpen || typeof window === 'undefined') return;

    const updateWidth = () => {
      if (pdfContainerRef.current) {
        const containerWidth = pdfContainerRef.current.clientWidth;
        const viewportWidth = window.innerWidth;
        const availableWidth = containerWidth - 40;

        let calculatedWidth;
        if (viewportWidth < 768) {
          calculatedWidth = Math.min(availableWidth, 600);
        } else if (viewportWidth < 1024) {
          calculatedWidth = Math.min(availableWidth, 700);
        } else if (viewportWidth < 1285) {
          calculatedWidth = Math.min(availableWidth * 0.9, 750);
        } else {
          calculatedWidth = Math.min(availableWidth, 800);
        }

        setPdfContainerWidth(Math.max(300, calculatedWidth));
      }
    };

    requestAnimationFrame(() => {
      updateWidth();
    });

    const timeoutId = setTimeout(updateWidth, 100);
    const resizeObserver = new ResizeObserver(updateWidth);
    if (pdfContainerRef.current) {
      resizeObserver.observe(pdfContainerRef.current);
    }

    window.addEventListener('resize', updateWidth);

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateWidth);
    };
  }, [isOpen, isPreviewMode]);

  useEffect(() => {
    hasScrolledForPlacement.current = false;
    setHasOpenedDrawer(false);
    setIsDrawerOpen(false);
    resetSignatureState();
  }, [document?.id, resetSignatureState]);

  useEffect(() => {
    if (isPreviewMode) return;
    const tourFlag = sessionStorage.getItem('showDocumentTour');
    setShouldShowTour(tourFlag === 'true');
  }, [isPreviewMode]);

  useEffect(() => {
    if (!isOpen || isPreviewMode || !shouldShowTour) return;

    const styleId = 'driverjs-custom-styles';
    if (typeof window !== 'undefined' && !window.document.getElementById(styleId)) {
      const style = window.document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .driver-popover {
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif !important;
          background: #e0f2fe !important;
          border: 1px solid #bae6fd !important;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
          min-width: 350px !important;
          max-width: 450px !important;
        }
        .driver-popover-title {
          font-family: 'Poppins', sans-serif !important;
          font-weight: 600 !important;
          color: #0c4a6e !important;
        }
        .driver-popover-description {
          font-family: 'Poppins', sans-serif !important;
          color: #075985 !important;
        }
        .driver-popover-footer {
          font-family: 'Poppins', sans-serif !important;
          display: flex !important;
          align-items: center !important;
          justify-content: flex-start !important;
          gap: 12px !important;
          flex-wrap: nowrap !important;
        }
        .driver-popover-close-btn {
          font-family: 'Poppins', sans-serif !important;
          color: #0c4a6e !important;
          font-size: 18px !important;
          font-weight: 600 !important;
          cursor: pointer !important;
          opacity: 0.8 !important;
          transition: opacity 0.2s !important;
        }
        .driver-popover-close-btn:hover {
          opacity: 1 !important;
          color: #075985 !important;
        }
        .driver-popover-btn {
          font-family: 'Poppins', sans-serif !important;
          font-weight: 500 !important;
          background: #0284c7 !important;
          color: white !important;
          border: none !important;
          padding: 8px 16px !important;
          border-radius: 6px !important;
          transition: background 0.2s !important;
        }
        .driver-popover-btn:hover {
          background: #0369a1 !important;
        }
        .driver-popover-btn.driver-popover-btn-secondary {
          background: #cbd5e1 !important;
          color: #475569 !important;
        }
        .driver-popover-btn.driver-popover-btn-secondary:hover {
          background: #94a3b8 !important;
        }
        .driver-popover-progress-text {
          font-family: 'Poppins', sans-serif !important;
          color: #075985 !important;
          font-weight: 500 !important;
        }
      `;
      window.document.head.appendChild(style);
    }

    const handleDriverButtonClick = (e) => {
      const target = e.target;
      const isCloseBtn = target.classList.contains('driver-popover-close-btn') ||
                         target.closest('.driver-popover-close-btn');
      const nextBtn = target.classList.contains('driver-popover-next-btn')
                      ? target
                      : target.closest('.driver-popover-next-btn');
      const skipBtn = target.classList.contains('driver-popover-skip-btn') ||
                      target.closest('.driver-popover-skip-btn');
      const isDoneBtn = nextBtn && (nextBtn.textContent.trim() === 'I got it' || nextBtn.innerText.trim() === 'I got it');

      if (isCloseBtn || isDoneBtn || skipBtn) {
        sessionStorage.removeItem('showDocumentTour');
        setShouldShowTour(false);
        setTimeout(() => {
          if (driverObjRef.current) {
            try {
              driverObjRef.current.destroy();
            } catch (err) {
            }
            driverObjRef.current = null;
          }
        }, 100);
      }
    };

    if (typeof window !== 'undefined' && window.document) {
      window.document.addEventListener('click', handleDriverButtonClick, true);
    }

    const timer = setTimeout(() => {
      let driverInstance = null;
      
      const driverObj = driver({
          showProgress: true,
          showButtons: ['next', 'previous', 'close'],
          allowClose: true,
          doneBtnText: 'I got it',
          onPopoverRender: () => {
            if (typeof window === 'undefined' || !window.document) return;
            
            setTimeout(() => {
              const popover = window.document.querySelector('.driver-popover');
              if (!popover) return;
              
              const footer = popover.querySelector('.driver-popover-footer');
              if (footer && !footer.querySelector('.driver-popover-skip-btn')) {
                footer.style.display = 'flex';
                footer.style.alignItems = 'center';
                footer.style.justifyContent = 'flex-start';
                footer.style.gap = '12px';
                footer.style.flexWrap = 'nowrap';
                
                const skipBtn = window.document.createElement('button');
                skipBtn.className = 'driver-popover-skip-btn';
                skipBtn.textContent = 'Skip the wizard';
                skipBtn.style.cssText = `
                  display: inline-flex;
                  align-items: center;
                  justify-content: center;
                  background: transparent;
                  border: 1px solid #cbd5e1;
                  color: #475569;
                  padding: 8px 16px;
                  border-radius: 6px;
                  font-family: 'Poppins', sans-serif;
                  font-weight: 500;
                  font-size: 14px;
                  line-height: 1.5;
                  cursor: pointer;
                  transition: all 0.2s;
                  white-space: nowrap;
                  flex-shrink: 0;
                  margin: 0;
                  vertical-align: middle;
                `;
                skipBtn.onmouseenter = () => {
                  skipBtn.style.background = '#f1f5f9';
                  skipBtn.style.borderColor = '#94a3b8';
                };
                skipBtn.onmouseleave = () => {
                  skipBtn.style.background = 'transparent';
                  skipBtn.style.borderColor = '#cbd5e1';
                };
                skipBtn.onclick = () => {
                  sessionStorage.removeItem('showDocumentTour');
                  setShouldShowTour(false);
                  const instance = driverInstance || driverObjRef.current;
                  if (instance) {
                    try {
                      instance.destroy();
                    } catch (err) {
                    }
                  }
                  if (driverObjRef.current) {
                    driverObjRef.current = null;
                  }
                };
                footer.insertBefore(skipBtn, footer.firstChild);
              }
            }, 0);
          },
          onDestroyStarted: () => {
            sessionStorage.removeItem('showDocumentTour');
            setShouldShowTour(false);
          },
          steps: [
            {
              element: '[data-tour="signature-name-input"]',
              popover: {
                title: 'Step 1: Create Your Signature',
                description: 'Type your full name here to generate signature style options.',
                side: 'left',
                align: 'start',
              },
            },
            {
              element: '[data-tour="signature-templates"]',
              popover: {
                title: 'Choose Signature Style',
                description: 'Select a signature style that you like.',
                side: 'left',
                align: 'start',
              },
            },
            {
              element: '[data-tour="place-signature-button"]',
              popover: {
                title: 'Step 2: Place Your Signature',
                description: 'Click "Place Signature" button to start placing your signature on the document.',
                side: 'left',
                align: 'center',
              },
            },
            {
              element: '[data-tour="pdf-viewer"]',
              popover: {
                title: 'Click on the PDF',
                description: 'Now click anywhere on the PDF document where you want your signature to appear. The signature will be centered around your click point.',
                side: 'right',
                align: 'center',
              },
            },
            {
              element: '[data-tour="document-fields-section"]',
              popover: {
                title: 'Complete Other Fields',
                description: 'Do the same for your name and signing date fields: enter the values, click "Place" buttons, and click on the PDF to position them. Each field will be centered around your click point.',
                side: 'left',
                align: 'start',
              },
            },
            {
              element: '[data-tour="submit-button"]',
              popover: {
                title: 'Step 3: Submit Document',
                description: 'Once all required fields are placed, click here to save your signed document.',
                side: 'left',
                align: 'start',
              },
            },
          ],
          onHighlightStarted: (element, step, options) => {
            if (typeof window !== 'undefined' && window.document) {
              setTimeout(() => {
                const popover = window.document.querySelector('.driver-popover');
                if (!popover) return;
                
                const footer = popover.querySelector('.driver-popover-footer');
                if (footer && !footer.querySelector('.driver-popover-skip-btn')) {
                  footer.style.display = 'flex';
                  footer.style.alignItems = 'center';
                  footer.style.justifyContent = 'flex-start';
                  footer.style.gap = '12px';
                  footer.style.flexWrap = 'nowrap';
                  
                  const skipBtn = window.document.createElement('button');
                  skipBtn.className = 'driver-popover-skip-btn';
                  skipBtn.textContent = 'Skip the wizard';
                  skipBtn.style.cssText = `
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    background: transparent;
                    border: 1px solid #cbd5e1;
                    color: #475569;
                    padding: 8px 16px;
                    border-radius: 6px;
                    font-family: 'Poppins', sans-serif;
                    font-weight: 500;
                    font-size: 14px;
                    line-height: 1.5;
                    cursor: pointer;
                    transition: all 0.2s;
                    white-space: nowrap;
                    flex-shrink: 0;
                    margin: 0;
                    vertical-align: middle;
                  `;
                  skipBtn.onmouseenter = () => {
                    skipBtn.style.background = '#f1f5f9';
                    skipBtn.style.borderColor = '#94a3b8';
                  };
                  skipBtn.onmouseleave = () => {
                    skipBtn.style.background = 'transparent';
                    skipBtn.style.borderColor = '#cbd5e1';
                  };
                  skipBtn.onclick = () => {
                    sessionStorage.removeItem('showDocumentTour');
                    setShouldShowTour(false);
                    const instance = driverInstance || driverObjRef.current;
                    if (instance) {
                      try {
                        instance.destroy();
                      } catch (err) {
                      }
                    }
                    if (driverObjRef.current) {
                      driverObjRef.current = null;
                    }
                  };
                  footer.insertBefore(skipBtn, footer.firstChild);
                }
              }, 100);
            }
            
            const tourAttr = element?.getAttribute?.('data-tour');
            
            if (tourAttr === 'place-signature-button') {
              setTimeout(() => {
                if (element) {
                  element.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'center',
                    inline: 'nearest'
                  });
                }
              }, 200);
            }
            
            if (tourAttr === 'pdf-viewer') {
              setTimeout(() => {
                if (numPages > 0) {
                  const lastPageNumber = numPages;
                  const pageElement = pdfPageRefs.current[lastPageNumber];
                  if (pageElement) {
                    pageElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
                  } else if (pdfContainerRef.current) {
                    pdfContainerRef.current.scrollTop = pdfContainerRef.current.scrollHeight;
                  }
                }
              }, 300);
            }
            
            if (tourAttr === 'signature-name-input') {
              setTimeout(() => {
                if (!typedName || typedName.trim() === '') {
                  setTypedName('test');
                }
              }, 200);
            }
          },
          onDestroyed: () => {
            if (driverObjRef.current) {
              driverObjRef.current = null;
            }
          },
        });

        driverInstance = driverObj;
        driverObjRef.current = driverObj;
        driverObj.drive();
    }, 500);

    return () => {
      clearTimeout(timer);
      if (typeof window !== 'undefined' && window.document) {
        window.document.removeEventListener('click', handleDriverButtonClick, true);
      }
      if (driverObjRef.current) {
        try {
          driverObjRef.current.destroy();
        } catch (err) {
        }
        driverObjRef.current = null;
      }
    };
  }, [isOpen, isPreviewMode, shouldShowTour, numPages, pdfPageRefs, pdfContainerRef, typedName, setTypedName]);

  if (!isOpen || !document) {
    return null;
  }

  const hasUnsavedChanges = !isPreviewMode && (
    signaturePosition ||
    textBlocks.some(block => block.placement || (block.placements && block.placements.length > 0))
  );

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <style>{`
        .thin-scrollbar {
          overflow-y: auto !important;
          scrollbar-width: thin;
          scrollbar-color: #cbd5e1 #f1f5f9;
        }
        .thin-scrollbar::-webkit-scrollbar {
          width: 8px;
          display: block;
        }
        .thin-scrollbar::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 4px;
        }
        .thin-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
        }
        .thin-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }

        /* Responsive adjustments for screens below 1285px */
        @media (max-width: 1285px) {
          .pdf-page-wrapper {
            max-width: 100%;
          }
        }

        @media (max-width: 1024px) and (min-width: 768px) {
          .pdf-page {
            margin: 0 auto;
          }
        }

        @media (max-width: 768px) {
          .thin-scrollbar::-webkit-scrollbar {
            width: 4px;
          }
        }
      `}</style>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={() => onClose(false)}
      />

      <div className="fixed inset-0 flex items-center justify-center p-2 sm:p-4">
        <div className="relative bg-white w-full sm:w-[98vw] md:w-[96vw] lg:w-[94vw] xl:w-[90vw] max-w-[1800px] h-[95vh] sm:h-[96vh] flex flex-col shadow-2xl rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-3 sm:px-4 md:px-5 lg:px-6 py-2 sm:py-3 md:py-3.5 lg:py-4 border-b border-gray-200 flex-shrink-0">
            <h2 className="text-sm sm:text-base md:text-lg lg:text-xl font-semibold text-gray-900 truncate pr-2">
              {isPreviewMode ? 'View' : 'Sign'}: {document.name}
              {document?.meta?.is_signable === false && document?.status !== 'Completed' && (
                <span className="ml-2 text-xs font-normal text-gray-500">(View Only)</span>
              )}
            </h2>
            <button
              onClick={() => onClose(false)}
              className="text-gray-400 hover:text-gray-500 transition-colors"
              aria-label="Close modal"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="flex-1 flex flex-col md:flex-col lg:flex-row overflow-hidden">
            <PDFViewer
              pdfUrl={pdfUrl}
              numPages={numPages}
              error={error}
              isLoadingPdf={isLoadingPdf}
              pdfContainerWidth={pdfContainerWidth}
              pdfContainerRef={pdfContainerRef}
              isPreviewMode={isPreviewMode}
              placementTarget={placementTarget}
              signatureDraft={signatureDraft}
              signaturePosition={signaturePosition}
              textBlocks={textBlocks}
              pdfPageRefs={pdfPageRefs}
              pdfPages={pdfPages}
              onDocumentLoadSuccess={onDocumentLoadSuccess}
              onDocumentLoadError={onDocumentLoadError}
              onPlacementClick={handlePlacementClick}
              registerPageRef={registerPageRef}
              onRetry={handleRetry}
            />

            {!isPreviewMode && (
              <div className="hidden lg:flex lg:flex-col lg:overflow-hidden lg:min-h-0">
                <SigningSidebar
                  instructionsExpanded={instructionsExpanded}
                  onToggleInstructions={() => setInstructionsExpanded((prev) => !prev)}
                  documentName={document.name}
                  textBlocks={textBlocks}
                  isLoadingFields={isLoadingFields}
                  onTextBlockValueChange={handleTextBlockValueChange}
                  onTextBlockPlace={(blockId) => handleFindingPlacement('text', blockId)}
                  onTextBlockReset={handleResetTextPlacement}
                  queuePlacementForBlock={queuePlacementForBlock}
                  typedName={typedName}
                  typedTemplates={typedTemplates}
                  typedSelectedTemplate={typedSelectedTemplate}
                  onTypedNameChange={setTypedName}
                  onTemplateSelect={setTypedSelectedTemplate}
                  signaturePosition={signaturePosition}
                  hasSignatureImage={hasSignatureImage()}
                  onPlaceSignature={() => handleFindingPlacement('signature')}
                  onResetSignature={resetSignaturePlacement}
                  isSignatureRequired={isSignatureRequired}
                />
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 sm:gap-3 px-3 sm:px-4 md:px-5 lg:px-6 py-2 sm:py-3 md:py-3.5 lg:py-4 border-t border-gray-200 flex-shrink-0">
            <button
              onClick={() => {
                if (hasUnsavedChanges) {
                  setShowDiscardConfirmation(true);
                } else {
                  onClose(false);
                }
              }}
              className="px-3 sm:px-4 py-1.5 sm:py-2 text-sm sm:text-base text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            >
              {hasUnsavedChanges ? 'Discard Changes' : 'Close'}
            </button>
            {!isPreviewMode && (
              <>
                {!hasOpenedDrawer && (
                  <button
                    onClick={handleSignNowClick}
                    className="lg:hidden px-3 sm:px-4 py-1.5 sm:py-2 text-sm sm:text-base bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
                  >
                    Sign Now
                  </button>
                )}
                {hasOpenedDrawer && (
                  <button
                    data-tour="submit-button"
                    onClick={handleSubmitDocument}
                    disabled={!canSubmitDocument || submitting}
                    className="lg:hidden px-3 sm:px-4 py-1.5 sm:py-2 text-sm sm:text-base bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  >
                    {submitting ? 'Submitting...' : 'Submit Document'}
                  </button>
                )}
                <button
                  data-tour="submit-button"
                  onClick={handleSubmitDocument}
                  disabled={!canSubmitDocument || submitting}
                  className="hidden lg:block px-3 sm:px-4 py-1.5 sm:py-2 text-sm sm:text-base bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  {submitting ? 'Submitting...' : 'Submit Document'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showDiscardConfirmation}
        onClose={() => setShowDiscardConfirmation(false)}
        onConfirm={() => onClose(false)}
        title="Discard Changes?"
        message="Are you sure you want to discard all changes? All signature and field placements will be lost."
        confirmText="Discard Changes"
        cancelText="Keep Editing"
        confirmButtonClass="bg-red-500 hover:bg-red-600"
      />

      {!isPreviewMode && (
        <BottomDrawer isOpen={isDrawerOpen} onClose={handleDrawerClose}>
          <SigningSidebar
            instructionsExpanded={instructionsExpanded}
            onToggleInstructions={() => setInstructionsExpanded((prev) => !prev)}
            documentName={document.name}
            textBlocks={textBlocks}
            isLoadingFields={isLoadingFields}
            onTextBlockValueChange={handleTextBlockValueChange}
            onTextBlockPlace={(blockId) => handleFindingPlacement('text', blockId)}
            onTextBlockReset={handleResetTextPlacement}
            queuePlacementForBlock={queuePlacementForBlock}
            typedName={typedName}
            typedTemplates={typedTemplates}
            typedSelectedTemplate={typedSelectedTemplate}
            onTypedNameChange={setTypedName}
            onTemplateSelect={setTypedSelectedTemplate}
            signaturePosition={signaturePosition}
            hasSignatureImage={hasSignatureImage()}
            onPlaceSignature={() => handleFindingPlacement('signature')}
            onResetSignature={resetSignaturePlacement}
            isSignatureRequired={isSignatureRequired}
          />
        </BottomDrawer>
      )}
    </div>
  );
}

export default DocumentSigningModal;

